import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import Loader from 'shared/common/loader';
import { dashboardStore } from 'store/DashboardStore';
import useSwipe from 'pages/buy-coins-bars/hooks/useSwipe';
import BuyCoinsBarsService from "../../services/buy-coins-bars.service";
import MetalSection from './MetalSection';
import "../../styles/Step1.css";
import buyCoinsBarsService from '../../services/buy-coins-bars.service';
import FAQAccordion from 'shared/common/faq';

const Step1 = ({ nextStep }) => {
  const goldRef = useRef(null);
  const silverRef = useRef(null);

  const [customerHolding] = dashboardStore((state) => [state.customerHolding]);

  // console.log(customerHolding,"@customerHolding");

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [balances, setBalances] = useState({ gold: 0, silver: 0 });
  const [faqs, setFaqs] = useState([]);
  const [screen, setScreen] = useState('desktop');

  useSwipe(goldRef);
  useSwipe(silverRef);

  //  Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 768) setScreen('mobile');
      else if (width <= 1024) setScreen('tablet');
      else setScreen('desktop');
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  //  Fetch all products only once
  useEffect(() => {
    const getAllProducts = async () => {
      try {
        setLoading(true);
        const response = await BuyCoinsBarsService.getAllProducts();
        if (response?.status === 200 && response?.data?.result) {
          setProducts(response.data.result);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    getAllProducts();
  }, []);

  // Helper function
  const getAvailableQuantity = (item) => {
    if (!item || typeof item.quantity !== 'number' || typeof item.lockQuantity !== 'number') {
      return 0;
    }

    const available =
      item.lockQuantity > 0
        ? item.quantity
        : item.quantity - Math.abs(item.lockQuantity);

    return Number(Math.max(0, available).toFixed(4)); // numeric with 4 decimal precision
  };

  // useEffect
  useEffect(() => {
    if (customerHolding?.length) {
      const newBalances = customerHolding.reduce(
        (acc, item) => {
          const availableQty = getAvailableQuantity(item);

          if (item.metal === 'Au') acc.gold = availableQty;
          if (item.metal === 'Ag') acc.silver = availableQty;

          return acc;
        },
        { gold: 0, silver: 0 }
      );

      setBalances((prev) =>
        prev.gold === newBalances.gold && prev.silver === newBalances.silver
          ? prev
          : newBalances
      );
    }
  }, [customerHolding]);


  //  Memoize the function so its reference is stable
  const getSingleProduct = useCallback(async (itemCode) => {
    try {
      const response = await BuyCoinsBarsService.getSingleProduct(itemCode);
      if (response.status !== 200) throw new Error('Failed to fetch product');
      return response.data[0];
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }, []);

  //  Fetch FAQs once
  useEffect(() => {
    const getFaqList = async () => {
      try {
        const res = await buyCoinsBarsService.getFaqs();
        if (res?.status === 200 && Array.isArray(res?.data?.data)) {
          setFaqs(res.data.data);
        } else {
          console.warn("Unexpected FAQ response format:", res);
          setFaqs([]);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
        setFaqs([]);
      }
    };
    getFaqList();
  }, []);


  const goldProducts = useMemo(
    () => products.filter((p) => p.metalCode === 'Au'),
    [products]
  );

  const silverProducts = useMemo(
    () => products.filter((p) => p.metalCode === 'Ag'),
    [products]
  );

  const memoBalances = useMemo(() => balances, [balances.gold, balances.silver]);

  //  Stable props prevent MetalSection re-render storms
  return (
    <div className='step1-container'>
      {loading && <Loader />}

      <MetalSection
        metal="gold"
        refObj={goldRef}
        products={goldProducts}
        balances={memoBalances}
        screen={screen}
        nextStep={nextStep}
        getSingleProduct={getSingleProduct}
        loading={loading}
      />

      <MetalSection
        metal="silver"
        refObj={silverRef}
        products={silverProducts}
        balances={memoBalances}
        screen={screen}
        nextStep={nextStep}
        getSingleProduct={getSingleProduct}
        loading={loading}
      />

      <div className='delivery-faq-section'>
        <FAQAccordion faqs={faqs} />
      </div>
    </div>
  );
};

export default Step1;
